
import React, { useState, useRef, useEffect } from 'react';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import ProgressBar from '../components/ui/ProgressBar';
import Toast, { ToastType } from '../components/ui/Toast';
import { Upload, Search, Save, RefreshCw, Trash2, Plus, X, ChevronDown, ExternalLink } from 'lucide-react';
import { api } from '../services/api';
import { useSubscriptionError } from '../context/SubscriptionErrorContext';
import { handleApiError } from '../utils/errorHandling';

const DataManagementPage: React.FC = () => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { setSubscriptionError } = useSubscriptionError();

  // Toast notification state
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);
  const showToast = (message: string, type: ToastType) => {
    setToast({ message, type });
  };

  // --- Category Types and Schema from Preferences ---
  const [categoryTypes, setCategoryTypes] = useState<string[]>(['Product', 'Category', 'Free Text'])
  const [preferencesSchema, setPreferencesSchema] = useState<any>(null)
  const [isLoadingPreferences, setIsLoadingPreferences] = useState(true)

  // --- Single Item Manager State ---
  const [searchId, setSearchId] = useState('')
  const [searchType, setSearchType] = useState<string>('Product')
  const [isSearching, setIsSearching] = useState(false)
  const [searchResult, setSearchResult] = useState<any>(null)
  const [itemNotFound, setItemNotFound] = useState(false)
  const [editedData, setEditedData] = useState<any>(null)
  const [editedText, setEditedText] = useState('')
  const [jsonError, setJsonError] = useState<string | null>(null)
  const [resetCounter, setResetCounter] = useState(0)
  type ArrayItemType = {key: string, value: string, itemType?: 'string' | 'number' | 'boolean' | 'array', nestedItems?: ArrayItemType[]}
  type FieldType = {
    id: string
    name?: string
    value: string
    dataType: 'string' | 'number' | 'boolean' | 'mixed' | 'free-text' | 'array'
    category: 'Properties' | 'Meta Data' | 'Index' | 'Top'
    pairs?: ArrayItemType[]
    isFromSchema?: boolean
    canDelete?: boolean
  }
  const [newFields, setNewFields] = useState<FieldType[]>([])
  const [isSaving, setIsSaving] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [searchTypeDropdownOpen, setSearchTypeDropdownOpen] = useState(false)
  const [openFieldTypeDropdowns, setOpenFieldTypeDropdowns] = useState<{[key: string]: boolean}>({})
  const [openCategoryDropdowns, setOpenCategoryDropdowns] = useState<{[key: string]: boolean}>({})
  const [importTypeDropdownOpen, setImportTypeDropdownOpen] = useState(false)
  const [viewMode, setViewMode] = useState<'form' | 'json'>('form')
  const [expandModalData, setExpandModalData] = useState<{path: string, value: any, label: string} | null>(null)
  const [expandedValue, setExpandedValue] = useState<any>(null)
  const [nestedModalData, setNestedModalData] = useState<{path: string, value: any, label: string} | null>(null)
  const [nestedExpandedValue, setNestedExpandedValue] = useState<any>(null)

  // Load category types from preferences on mount
  useEffect(() => {
    loadCategoryTypesFromPreferences();
  }, []);

  const loadCategoryTypesFromPreferences = async () => {
    setIsLoadingPreferences(true);
    try {
      const response = await api.get<any>('/api/preferences');
      
      if (response?.dataManagement?.document) {
        const doc = response.dataManagement.document;
        
        // Load category types
        if (doc.categoryTypes && Array.isArray(doc.categoryTypes) && doc.categoryTypes.length > 0) {
          setCategoryTypes(doc.categoryTypes);
          // Set first type as default if current searchType is not in the list
          if (!doc.categoryTypes.includes(searchType)) {
            setSearchType(doc.categoryTypes[0]);
          }
        }
        
        // Store the entire schema for use when creating new items
        setPreferencesSchema(doc);
      }
    } catch (error: any) {
      // If preferences not found, keep default types
      console.log('No preferences found, using default category types');
    } finally {
      setIsLoadingPreferences(false);
    }
  };

  // Helper function to build fields from preferences schema
  const buildFieldsFromSchema = (categoryType: string) => {
    if (!preferencesSchema || !preferencesSchema.categories) return [];
    
    // Find the category matching the search type
    const category = preferencesSchema.categories.find(
      (cat: any) => cat.categoryType?.toLowerCase() === categoryType.toLowerCase()
    );
    
    if (!category) return [];
    
    const fields: any[] = [];
    
    // Add indexed properties
    if (category.indexedProperties && Array.isArray(category.indexedProperties)) {
      category.indexedProperties.forEach((prop: any) => {
        fields.push({
          id: `index_${prop.name || prop}`,
          name: prop.name || prop,
          value: '',
          dataType: prop.type || 'string',
          category: 'Index',
          isFromSchema: true,
          canDelete: true,
        });
      });
    }
    
    // Add metadata properties
    if (category.metadata && Array.isArray(category.metadata)) {
      category.metadata.forEach((prop: any) => {
        fields.push({
          id: `prop_${prop.name || prop}`,
          name: prop.name || prop,
          value: '',
          dataType: prop.type || 'string',
          category: 'Properties',
          isFromSchema: true,
          canDelete: true,
        });
      });
    }
    
    return fields;
  };

  const handleSearchItem = async () => {
    if (!searchId.trim()) {
      showToast('Please enter an ID to search', 'error');
      return
    }
    
    setIsSearching(true)
    setSearchResult(null)
    setItemNotFound(false)
    setEditedData(null)
    setNewFields([])
    
    try {
      const result = await api.getDocument(searchId);
      setSearchResult(result);
      setEditedData(JSON.parse(JSON.stringify(result))); // Deep clone
      setEditedText(JSON.stringify(result, null, 2));
      setJsonError(null);
      showToast('Document loaded successfully', 'success');
    } catch (err: any) {
      if (err.code === 'DOCUMENT__NOT_FOUND' || err.message?.includes('not found') || err.message?.includes('404')) {
        setItemNotFound(true);
        // Load schema fields for the selected category type
        const schemaFields = buildFieldsFromSchema(searchType);
        setNewFields(schemaFields);
        showToast('Document not found - Schema loaded for new item creation', 'info');
      } else {
        handleApiError(err, (m) => showToast(`Search failed: ${m}`, 'error'), setSubscriptionError);
      }
    } finally {
      setIsSearching(false);
    }
  }

  const handleRefreshData = () => {
    // Just reload the current search
    if (searchId.trim()) {
      handleSearchItem();
    } else {
      showToast('No document loaded to refresh', 'error');
    }
  }

  const handleTextChange = (newText: string) => {
    setEditedText(newText)
    try {
      const parsed = JSON.parse(newText)
      setEditedData(parsed)
      setJsonError(null)
    } catch (err: any) {
      setJsonError(err.message)
    }
  }

  const getNestedValue = (obj: any, path: string): any => {
    return path.split('.').reduce((current, key) => current?.[key], obj)
  }

  const setNestedValue = (obj: any, path: string, value: any): any => {
    const keys = path.split('.')
    const newObj = JSON.parse(JSON.stringify(obj))
    let current = newObj
    
    for (let i = 0; i < keys.length - 1; i++) {
      if (!current[keys[i]]) current[keys[i]] = {}
      current = current[keys[i]]
    }
    
    current[keys[keys.length - 1]] = value
    return newObj
  }

  const getFlattenedFields = (data: any): Array<{path: string, value: any, label: string, type: 'primitive' | 'object' | 'array'}> => {
    if (!data) return []
    
    const fields: Array<{path: string, value: any, label: string, type: 'primitive' | 'object' | 'array'}> = []
    
    const getFieldType = (value: any): 'primitive' | 'object' | 'array' => {
      if (Array.isArray(value)) return 'array'
      if (value !== null && typeof value === 'object') return 'object'
      return 'primitive'
    }
    
    if (data._id !== undefined) fields.push({ path: '_id', value: data._id, label: 'MongoDB ID', type: getFieldType(data._id) })
    if (data.id !== undefined) fields.push({ path: 'id', value: data.id, label: 'ID', type: getFieldType(data.id) })
    if (data.__v !== undefined) fields.push({ path: '__v', value: data.__v, label: 'Version', type: getFieldType(data.__v) })
    if (data.type !== undefined) fields.push({ path: 'type', value: data.type, label: 'Type', type: getFieldType(data.type) })
    if (data.name !== undefined) fields.push({ path: 'name', value: data.name, label: 'Name', type: getFieldType(data.name) })
    if (data.deleted !== undefined) fields.push({ path: 'deleted', value: data.deleted, label: 'Deleted', type: getFieldType(data.deleted) })
    if (data.createdAt !== undefined) fields.push({ path: 'createdAt', value: data.createdAt, label: 'Created At', type: getFieldType(data.createdAt) })
    if (data.updatedAt !== undefined) fields.push({ path: 'updatedAt', value: data.updatedAt, label: 'Updated At', type: getFieldType(data.updatedAt) })
    // RAG properties are intentionally excluded from display
    
    // Index properties
    if (data.indexProperties) {
      Object.entries(data.indexProperties).forEach(([key, value]) => {
        const label = key.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
        fields.push({ 
          path: `indexProperties.${key}`, 
          value, 
          label: `Index: ${label}`,
          type: getFieldType(value)
        })
      })
    }
    
    // Properties - iterate all properties dynamically
    if (data.properties) {
      Object.entries(data.properties).forEach(([key, value]) => {
        // Better label formatting for common patterns
        let label = key
        if (key === 'sku') label = 'SKU'
        else if (key === 'nameLT') label = 'Name (Localized)'
        else if (key === 'categoryKind') label = 'Category Kind'
        else if (key === 'beginPrice') label = 'Begin Price'
        else if (key === 'finalPrice') label = 'Final Price'
        else if (key === 'currency') label = 'Currency'
        else if (key === 'images') label = 'Images'
        else if (key === 'tags') label = 'Tags'
        else if (key === 'attributes') label = 'Attributes'
        else if (key === 'raw') label = 'Raw Data'
        else label = key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')
        
        fields.push({ 
          path: `properties.${key}`, 
          value, 
          label,
          type: getFieldType(value)
        })
      })
    }
    
    return fields
  }

  const handleOpenExpandModal = (field: {path: string, value: any, label: string}) => {
    setExpandModalData(field)
    setExpandedValue(JSON.parse(JSON.stringify(field.value))) // Deep clone
  }

  const handleCloseExpandModal = () => {
    setExpandModalData(null)
    setExpandedValue(null)
  }

  const handleUpdateExpandedField = (index: number | string, key: string, value: any) => {
    if (Array.isArray(expandedValue)) {
      const updated = [...expandedValue]
      if (typeof index === 'number') {
        updated[index] = { ...updated[index], [key]: value }
      }
      setExpandedValue(updated)
    } else if (typeof expandedValue === 'object' && expandedValue !== null) {
      setExpandedValue({ ...expandedValue, [key]: value })
    }
  }

  const handleOpenNestedExpandModal = (key: string, value: any) => {
    // Open a nested modal for object/array within the current expanded modal
    setNestedModalData({ path: `${expandModalData?.path}.${key}`, value, label: key })
    setNestedExpandedValue(JSON.parse(JSON.stringify(value))) // Deep clone
  }

  const handleCloseNestedExpandModal = () => {
    setNestedModalData(null)
    setNestedExpandedValue(null)
  }

  const handleUpdateNestedExpandedField = (index: number | string, key: string, value: any) => {
    if (Array.isArray(nestedExpandedValue)) {
      const updated = [...nestedExpandedValue]
      if (typeof index === 'number') {
        updated[index] = { ...updated[index], [key]: value }
      }
      setNestedExpandedValue(updated)
    } else if (typeof nestedExpandedValue === 'object' && nestedExpandedValue !== null) {
      setNestedExpandedValue({ ...nestedExpandedValue, [key]: value })
    }
  }

  const handleAddNestedExpandedArrayItem = () => {
    if (Array.isArray(nestedExpandedValue)) {
      setNestedExpandedValue([...nestedExpandedValue, {}])
    }
  }

  const handleRemoveNestedExpandedArrayItem = (index: number) => {
    if (Array.isArray(nestedExpandedValue)) {
      setNestedExpandedValue(nestedExpandedValue.filter((_, i) => i !== index))
    }
  }

  const handleSaveNestedExpandedModal = () => {
    if (!nestedModalData || !expandModalData) return
    
    // Update the parent modal's expandedValue with the nested changes
    const nestedKey = nestedModalData.path.split('.').pop()
    if (nestedKey) {
      const updatedExpandedValue = { ...expandedValue, [nestedKey]: nestedExpandedValue }
      setExpandedValue(updatedExpandedValue)
    }
    
    handleCloseNestedExpandModal()
    showToast('Nested field updated successfully', 'success')
  }

  const isFieldDisabledInRaw = (fieldKey: string): boolean => {
    // Check if this modal is editing the 'raw' object and if the field exists outside of raw
    if (!expandModalData || !expandModalData.path.endsWith('raw')) return false
    
    // Get all top-level fields from the document (outside of raw)
    const topLevelFields = getFlattenedFields(editedData)
      .filter(f => !f.path.includes('raw'))
      .map(f => f.path.split('.').pop())
    
    return topLevelFields.includes(fieldKey)
  }

  const handleAddExpandedArrayItem = () => {
    if (Array.isArray(expandedValue)) {
      setExpandedValue([...expandedValue, {}])
    }
  }

  const handleRemoveExpandedArrayItem = (index: number) => {
    if (Array.isArray(expandedValue)) {
      setExpandedValue(expandedValue.filter((_, i) => i !== index))
    }
  }

  const handleSaveExpandedModal = () => {
    if (!expandModalData) return
    
    const updated = setNestedValue(editedData, expandModalData.path, expandedValue)
    setEditedData(updated)
    setEditedText(JSON.stringify(updated, null, 2))
    handleCloseExpandModal()
    showToast('Field updated successfully', 'success')
  }

  const renderExpandedFieldValue = (value: any, parentIndex?: number, parentKey?: string) => {
    if (value === null || value === undefined) {
      return (
        <input
          type="text"
          value=""
          readOnly
          placeholder="null"
          className="px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-border-color rounded text-sm text-text-secondary"
        />
      )
    }

    if (typeof value === 'boolean') {
      return (
        <select
          value={value.toString()}
          onChange={(e) => {
            if (parentIndex !== undefined && parentKey) {
              handleUpdateExpandedField(parentIndex, parentKey, e.target.value === 'true')
            }
          }}
          className="px-3 py-2  dark:bg-background bg-white border border-border-color rounded focus:outline-none focus:ring-2 focus:ring-primary dark:text-white text-gray-900"
        >
          <option value="true">true</option>
          <option value="false">false</option>
        </select>
      )
    }

    if (typeof value === 'number') {
      return (
        <input
          type="number"
          value={value}
          onChange={(e) => {
            if (parentIndex !== undefined && parentKey) {
              handleUpdateExpandedField(parentIndex, parentKey, parseFloat(e.target.value) || 0)
            }
          }}
          className="px-3 py-2  dark:bg-background bg-white border border-border-color rounded focus:outline-none focus:ring-2 focus:ring-primary dark:text-white text-gray-900"
        />
      )
    }

    if (typeof value === 'string') {
      return (
        <input
          type="text"
          value={value}
          onChange={(e) => {
            if (parentIndex !== undefined && parentKey) {
              handleUpdateExpandedField(parentIndex, parentKey, e.target.value)
            }
          }}
          className="px-3 py-2  dark:bg-background bg-white border border-border-color rounded focus:outline-none focus:ring-2 focus:ring-primary dark:text-white text-gray-900"
        />
      )
    }

    if (typeof value === 'object') {
      return (
        <div className="px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-border-color rounded text-sm text-text-secondary">
          {Array.isArray(value) ? `Nested Array [${value.length} items]` : 'Nested Object'}
        </div>
      )
    }

    return (
      <input
        type="text"
        value={String(value)}
        readOnly
        className="px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-border-color rounded text-sm text-text-secondary"
      />
    )
  }

  const handleFormFieldChange = (path: string, value: any) => {
    const updated = setNestedValue(editedData, path, value)
    setEditedData(updated)
    setEditedText(JSON.stringify(updated, null, 2))
  }

  const handleSaveItem = async () => {
    if (!editedData || jsonError) {
      showToast('Please fix JSON errors before saving', 'error');
      return
    }
    
    setIsSaving(true)
    try {
      // Convert type to snake_case if needed (e.g., "Product" -> "product")
      const documentToSave = {
        ...editedData,
        type: editedData.type?.toLowerCase() || searchType.toLowerCase()
      };
      
      const response = await api.upsertDocuments([documentToSave]);
      
      if (response.documents > 0) {
        showToast('Document saved successfully!', 'success');
        // Refresh from server to get latest version
        const refreshed = await api.getDocument(editedData.id);
        setSearchResult(refreshed);
        setEditedData(JSON.parse(JSON.stringify(refreshed)));
        setEditedText(JSON.stringify(refreshed, null, 2));
      } else {
        showToast('No changes were made', 'error');
      }
    } catch (err: any) {
      handleApiError(err, (m) => showToast(`Save failed: ${m}`, 'error'), setSubscriptionError);
    } finally {
      setIsSaving(false)
    }
  }

  const handleDeleteItem = async () => {
    if (!editedData || !confirm(`Are you sure you want to delete this ${searchType}?`)) return
    
    setIsDeleting(true)
    try {
      await api.deleteDocument(editedData.id);
      
      showToast('Document deleted successfully!', 'success');
      setSearchResult(null);
      setEditedData(null);
      setSearchId('');
    } catch (err: any) {
      handleApiError(err, (m) => showToast(`Delete failed: ${m}`, 'error'), setSubscriptionError);
    } finally {
      setIsDeleting(false)
    }
  }

  const handleAddNewField = () => {
    const newField = {
      id: `field_${Date.now()}`,
      value: '',
      dataType: 'string' as const,
      category: 'Properties' as const,
      pairs: [{ key: '', value: '' }]
    }
    setNewFields([...newFields, newField])
  }

  const handleAddPairToField = (fieldId: string, parentPath?: number[]) => {
    setNewFields(newFields.map(f => {
      if (f.id !== fieldId) return f
      
      if (!parentPath || parentPath.length === 0) {
        // Add to root level
        return { ...f, pairs: [...(f.pairs || []), { key: '', value: '', itemType: 'string' as const }] }
      }
      
      // Add to nested array
      const newPairs = JSON.parse(JSON.stringify(f.pairs || []))
      let current: any = newPairs
      for (let i = 0; i < parentPath.length - 1; i++) {
        current = current[parentPath[i]].nestedItems
      }
      const lastIndex = parentPath[parentPath.length - 1]
      if (!current[lastIndex].nestedItems) {
        current[lastIndex].nestedItems = []
      }
      current[lastIndex].nestedItems.push({ key: '', value: '', itemType: 'string' as const })
      return { ...f, pairs: newPairs }
    }))
  }

  const handleRemovePairFromField = (fieldId: string, pairIndex: number, parentPath?: number[]) => {
    setNewFields(newFields.map(f => {
      if (f.id !== fieldId) return f
      
      if (!parentPath || parentPath.length === 0) {
        // Remove from root level
        return { ...f, pairs: (f.pairs || []).filter((_, idx) => idx !== pairIndex) }
      }
      
      // Remove from nested array
      const newPairs = JSON.parse(JSON.stringify(f.pairs || []))
      let current: any = newPairs
      for (let i = 0; i < parentPath.length; i++) {
        if (i === parentPath.length - 1) {
          current[parentPath[i]].nestedItems = current[parentPath[i]].nestedItems.filter((_: any, idx: number) => idx !== pairIndex)
        } else {
          current = current[parentPath[i]].nestedItems
        }
      }
      return { ...f, pairs: newPairs }
    }))
  }

  const handleUpdatePair = (fieldId: string, pairIndex: number, key: string, value: string, parentPath?: number[]) => {
    setNewFields(newFields.map(f => {
      if (f.id !== fieldId) return f
      
      if (!parentPath || parentPath.length === 0) {
        // Update at root level
        return { 
          ...f, 
          pairs: (f.pairs || []).map((p, idx) => 
            idx === pairIndex ? { ...p, key, value } : p
          )
        }
      }
      
      // Update nested item
      const newPairs = JSON.parse(JSON.stringify(f.pairs || []))
      let current: any = newPairs
      for (let i = 0; i < parentPath.length; i++) {
        if (i === parentPath.length - 1) {
          current[parentPath[i]].nestedItems[pairIndex] = { ...current[parentPath[i]].nestedItems[pairIndex], key, value }
        } else {
          current = current[parentPath[i]].nestedItems
        }
      }
      return { ...f, pairs: newPairs }
    }))
  }

  const handleUpdateItemType = (fieldId: string, pairIndex: number, itemType: 'string' | 'number' | 'boolean' | 'array', parentPath?: number[]) => {
    setNewFields(newFields.map(f => {
      if (f.id !== fieldId) return f
      
      if (!parentPath || parentPath.length === 0) {
        // Update at root level
        return { 
          ...f, 
          pairs: (f.pairs || []).map((p, idx) => 
            idx === pairIndex ? { ...p, itemType, value: '', nestedItems: itemType === 'array' ? [] : undefined } : p
          )
        }
      }
      
      // Update nested item
      const newPairs = JSON.parse(JSON.stringify(f.pairs || []))
      let current: any = newPairs
      for (let i = 0; i < parentPath.length; i++) {
        if (i === parentPath.length - 1) {
          current[parentPath[i]].nestedItems[pairIndex] = { 
            ...current[parentPath[i]].nestedItems[pairIndex], 
            itemType, 
            value: '', 
            nestedItems: itemType === 'array' ? [] : undefined 
          }
        } else {
          current = current[parentPath[i]].nestedItems
        }
      }
      return { ...f, pairs: newPairs }
    }))
  }

  const handleRemoveNewField = (id: string) => {
    setNewFields(newFields.filter(f => f.id !== id))
  }

  const handleUpdateNewField = (id: string, updates: Partial<typeof newFields[0]>) => {
    setNewFields(newFields.map(f => f.id === id ? { ...f, ...updates } : f))
  }

  // Helper to parse array value string into array
  const parseArrayValue = (value: string): any[] => {
    if (!value || !value.trim()) return [];
    
    // Try JSON parse first
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return parsed;
    } catch {}
    
    // Fall back to comma-separated
    return value.split(',').map(v => v.trim()).filter(v => v);
  };

  // Helper to add item to array field at any depth
  const handleAddArrayItem = (fieldId: string, path: number[] = []) => {
    const field = newFields.find(f => f.id === fieldId);
    if (!field) return;
    
    const currentArray = parseArrayValue(field.value || '');
    
    if (path.length === 0) {
      // Add to root array
      currentArray.push('');
    } else {
      // Navigate to nested array and add item
      let target = currentArray;
      for (let i = 0; i < path.length; i++) {
        target = target[path[i]];
      }
      if (Array.isArray(target)) {
        target.push('');
      }
    }
    
    handleUpdateNewField(fieldId, { value: JSON.stringify(currentArray, null, 2) });
  };

  // Helper to remove item from array field at any depth
  const handleRemoveArrayItem = (fieldId: string, path: number[]) => {
    const field = newFields.find(f => f.id === fieldId);
    if (!field) return;
    
    const currentArray = parseArrayValue(field.value || '');
    
    if (path.length === 1) {
      // Remove from root
      currentArray.splice(path[0], 1);
    } else {
      // Navigate to parent and remove item
      let target = currentArray;
      for (let i = 0; i < path.length - 1; i++) {
        target = target[path[i]];
      }
      if (Array.isArray(target)) {
        target.splice(path[path.length - 1], 1);
      }
    }
    
    handleUpdateNewField(fieldId, { value: currentArray.length > 0 ? JSON.stringify(currentArray, null, 2) : '' });
  };

  // Helper to update specific array item at any depth
  const handleUpdateArrayItem = (fieldId: string, path: number[], newValue: any) => {
    const field = newFields.find(f => f.id === fieldId);
    if (!field) return;
    
    const currentArray = parseArrayValue(field.value || '');
    
    // Navigate to parent and update item
    let target = currentArray;
    for (let i = 0; i < path.length - 1; i++) {
      target = target[path[i]];
    }
    target[path[path.length - 1]] = newValue;
    
    handleUpdateNewField(fieldId, { value: JSON.stringify(currentArray, null, 2) });
  };

  // Helper to convert array item to specific type
  const handleConvertArrayItemType = (fieldId: string, path: number[], newType: 'string' | 'number' | 'boolean' | 'array') => {
    const field = newFields.find(f => f.id === fieldId);
    if (!field) return;
    
    const currentArray = parseArrayValue(field.value || '');
    
    // Navigate to the item
    let target = currentArray;
    for (let i = 0; i < path.length - 1; i++) {
      target = target[path[i]];
    }
    
    // Convert to new type
    const currentValue = target[path[path.length - 1]];
    let newValue: any;
    
    switch (newType) {
      case 'array':
        newValue = [];
        break;
      case 'number':
        newValue = 0;
        break;
      case 'boolean':
        newValue = false;
        break;
      default:
        newValue = '';
    }
    
    target[path[path.length - 1]] = newValue;
    handleUpdateNewField(fieldId, { value: JSON.stringify(currentArray, null, 2) });
  };

  // Helper function to recursively convert array items with nested arrays
  const convertArrayItemToValue = (item: ArrayItemType): any => {
    if (item.itemType === 'array' && item.nestedItems) {
      // Recursively convert nested array items
      return item.nestedItems.map(convertArrayItemToValue);
    } else if (item.itemType === 'number') {
      return parseFloat(item.value) || 0;
    } else if (item.itemType === 'boolean') {
      return item.value === 'true';
    } else {
      // string type or fallback
      return item.value.trim();
    }
  }

  const handleCreateNewItem = async () => {
    if (!searchId.trim()) {
      showToast('Please enter an ID for the new document', 'error');
      return;
    }

    setIsSaving(true);
    try {
      // Build document structure
      const topLevel: Record<string, any> = {
        id: searchId,
        type: searchType.toLowerCase(),
        deleted: false
      };
      const indexProperties: Record<string, any> = {
        record_type: searchType.toLowerCase()
      };
      const properties: Record<string, any> = {};

      newFields.forEach((field) => {
        if (field.category === 'Top') {
          // Top-level fields
          if (field.id === 'top_name' && field.value.trim()) {
            topLevel.name = field.value.trim();
          } else if (field.id === 'top_deleted') {
            topLevel.deleted = field.value === 'true';
          }
        } else if (field.category === 'Index') {
          // Index properties - use field.name if available, otherwise extract from id
          const fieldName = field.name || field.id.replace(/^index_/, '');
          if (field.value && field.value.trim()) {
            let value: any = field.value.trim();
            if (field.dataType === 'number') value = parseFloat(value) || 0;
            if (field.dataType === 'boolean') value = value === 'true';
            if (field.dataType === 'array') {
              try {
                value = JSON.parse(value);
              } catch {
                value = field.value.split(',').map(v => v.trim());
              }
            }
            indexProperties[fieldName] = value;
          }
        } else if (field.category === 'Properties') {
          // Properties - use field.name if available, otherwise extract from id
          const fieldName = field.name || field.id.replace(/^prop_/, '');
          if (field.value && field.value.trim()) {
            let value: any = field.value.trim();
            if (field.dataType === 'number') value = parseFloat(value) || 0;
            if (field.dataType === 'boolean') value = value === 'true';
            if (field.dataType === 'array') {
              try {
                value = JSON.parse(value);
              } catch {
                value = field.value.split(',').map(v => v.trim());
              }
            }
            properties[fieldName] = value;
          }
        } else if (field.dataType === 'free-text') {
          // Free text goes directly to properties
          if (field.value.trim()) {
            properties.freeText = field.value;
          }
        } else if (field.dataType === 'array' && field.pairs && field.pairs.length > 0) {
          // Array type: first pair is the array name, rest are items
          const arrayName = field.pairs[0]?.key.trim();
          if (arrayName) {
            const arrayItems = field.pairs.slice(1).map(item => convertArrayItemToValue(item)).filter(item => {
              // Filter out empty strings, but keep false, 0, and empty arrays
              if (typeof item === 'string') return item !== '';
              return true;
            });
            // @ts-ignore
            const targetObj = (field.category === 'Properties' || field.category === 'Meta Data') ? properties : indexProperties;
            targetObj[arrayName] = arrayItems;
          }
        } else if (field.dataType === 'mixed' && field.pairs) {
          // Mixed type: process all key-value pairs
          field.pairs.forEach(pair => {
            if (pair.key.trim() && pair.value.trim()) {
              const targetObj = (field.category === 'Properties' || field.category === 'Meta Data') ? properties : indexProperties;
              targetObj[pair.key.trim()] = pair.value.trim();
            }
          });
        } else {
          // Single key-value for string/number/boolean custom fields
          const pairs = field.pairs || [{ key: '', value: field.value }];
          pairs.forEach(pair => {
            if (pair.key.trim() && pair.value.trim()) {
              let value: any = pair.value.trim();
              if (field.dataType === 'number') value = parseFloat(value) || 0;
              if (field.dataType === 'boolean') value = value.toLowerCase() === 'true';
              
              const targetObj = (field.category === 'Properties' || field.category === 'Meta Data') ? properties : indexProperties;
              targetObj[pair.key.trim()] = value;
            }
          });
        }
      });

      // Create the document with proper structure
      const newDocument: any = {
        ...topLevel,
        indexProperties,
        properties
      };

      const response = await api.upsertDocuments([newDocument]);
      
      if (response.documents > 0) {
        showToast('Document created successfully!', 'success');
        // Fetch the created document to get full server state
        const created = await api.getDocument(searchId);
        setSearchResult(created);
        setEditedData(JSON.parse(JSON.stringify(created)));
        setEditedText(JSON.stringify(created, null, 2));
        setItemNotFound(false);
        setNewFields([]);
        setJsonError(null);
      } else {
        showToast('Document was not created (may already exist)', 'error');
      }
    } catch (err: any) {
      handleApiError(err, (m) => showToast(`Create failed: ${m}`, 'error'), setSubscriptionError);
    } finally {
      setIsSaving(false);
    }
  }

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
  }

  // --- Documents Import (CSV/JSON/XML) ---
  const [importFiles, setImportFiles] = useState<File[]>([])
  const [importBatchId, setImportBatchId] = useState<string | null>(null)
  const [importIsReserving, setImportIsReserving] = useState(false)
  const [importIsUploading, setImportIsUploading] = useState(false)
  const [importIsTriggering, setImportIsTriggering] = useState(false)
  const [importProgress, setImportProgress] = useState<Record<string, number>>({})
  const [importUploadedKeys, setImportUploadedKeys] = useState<string[]>([])
  const [importFormat, setImportFormat] = useState<'auto'|'csv'|'json'|'xml'>('auto')

  const handleImportStartBatch = async () => {
    setImportIsReserving(true)
    try {
      const resp = await api.startRAGContextReplace()
      setImportBatchId(resp.batchId)
      if ((importFiles?.length || 0) > 0 && !importIsUploading) {
        void handleImportUploadAll(importFiles)
      }
    } catch (err: any) {
      handleApiError(err, (m) => showToast(`Failed to start import batch: ${m}`, 'error'), setSubscriptionError)
    } finally {
      setImportIsReserving(false)
    }
  }

  const handleImportFilesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const list = Array.from(event.target.files || [])
    if (list.length > 0) {
      const allowed = list.filter(f => /\.(csv|json|xml)$/i.test(f.name))
      setImportFiles(allowed)
      const initProg: Record<string, number> = {}
      allowed.forEach(f => (initProg[f.name] = 0))
      setImportProgress(initProg)
      setImportUploadedKeys([])
      if (fileInputRef.current) fileInputRef.current.value = ''
      if (importBatchId && allowed.length > 0 && !importIsUploading) {
        void handleImportUploadAll(allowed)
      }
    } else {
      setImportFiles([])
      setImportProgress({})
      setImportUploadedKeys([])
    }
  }

  async function uploadImportFileMultipart(batchId: string, file: File): Promise<string> {
    // Reuse same multipart flow
    const init = await api.initiateMultipartUpload({
      batchId, fileName: file.name, contentType: file.type || inferContentType(file.name), fileSize: file.size
    })
    const { uploadId, key, partSize } = init

    const totalParts = Math.ceil(file.size / partSize)
    let uploadedBytes = 0
    const parts: { ETag: string; PartNumber: number }[] = []

    for (let partNumber = 1; partNumber <= totalParts; partNumber++) {
      const start = (partNumber - 1) * partSize
      const end = Math.min(start + partSize, file.size)
      const blob = file.slice(start, end)

      const sign = await api.signMultipartUploadPart({ key, uploadId, partNumber })

      const etag = await new Promise<string>((resolve, reject) => {
        const xhr = new XMLHttpRequest()
        xhr.open('PUT', sign.url)
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            const previously = (partNumber - 1) * partSize
            const current = Math.min(previously + e.loaded, file.size)
            const pct = Math.floor((current / file.size) * 100)
            setImportProgress(prev => ({ ...prev, [file.name]: Math.min(100, pct) }))
          }
        }
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            const e = xhr.getResponseHeader('ETag')
            if (!e) return reject(new Error('Missing ETag header'))
            resolve(e)
          } else {
            reject(new Error(`S3 uploadPart failed: ${xhr.status}`))
          }
        }
        xhr.onerror = () => reject(new Error('Network error during uploadPart'))
        xhr.send(blob)
      })

      parts.push({ ETag: etag, PartNumber: partNumber })
      uploadedBytes += blob.size
      const pct = Math.floor((uploadedBytes / file.size) * 100)
      setImportProgress(prev => ({ ...prev, [file.name]: Math.min(100, pct) }))
    }

    await api.completeMultipartUpload({
      batchId,
      key,
      uploadId,
      parts,
      originalName: file.name,
      contentType: file.type || inferContentType(file.name),
      size: file.size
    })

    return key
  }

  function inferContentType(name: string): string {
    const lower = name.toLowerCase()
    if (lower.endsWith('.csv')) return 'text/csv'
    if (lower.endsWith('.json')) return 'application/json'
    if (lower.endsWith('.xml')) return 'application/xml'
    return 'application/octet-stream'
  }

  const handleImportUploadAll = async (providedFiles?: File[]) => {
    const filesToUpload = providedFiles && providedFiles.length > 0 ? providedFiles : importFiles
    if (!importBatchId || filesToUpload.length === 0) {
      showToast('Please start a batch and choose at least one CSV/JSON/XML file.', 'error')
      return
    }
    setImportIsUploading(true)
    setImportUploadedKeys([])
    try {
      const concurrency = 3
      let idx = 0
      const keys: string[] = []
      const worker = async () => {
        const i = idx++
        if (i >= filesToUpload.length) return
        const f = filesToUpload[i]
        try {
          const key = await uploadImportFileMultipart(importBatchId, f)
          keys.push(key)
        } catch (e: any) {
          handleApiError(e, (m) => showToast(`Failed uploading ${f.name}: ${m}`, 'error'), setSubscriptionError)
        }
        await worker()
      }
      await Promise.all(Array.from({ length: Math.min(concurrency, filesToUpload.length) }, () => worker()))
      setImportUploadedKeys(keys)
      showToast(`Uploaded ${keys.length} file(s) for import.`, 'success')
    } finally {
      setImportIsUploading(false)
    }
  }

  const handleTriggerImport = async () => {
    if (!importUploadedKeys.length) {
      showToast('No uploaded files to import.', 'error')
      return
    }
    setImportIsTriggering(true)
    try {
      await api.importDocumentsFromS3({ keys: importUploadedKeys, format: importFormat })
      showToast('Import triggered successfully. Check backend logs and MongoDB for progress.', 'success')
    } catch (e: any) {
      handleApiError(e, (m) => showToast(`Failed to trigger import: ${m}`, 'error'), setSubscriptionError)
    } finally {
      setImportIsTriggering(false)
    }
  }

  // Recursive component to render array items at any depth
  const renderArrayItem = (
    item: ArrayItemType, 
    itemIndex: number, 
    fieldId: string, 
    parentPath: number[] = [],
    depth: number = 0
  ): React.ReactNode => {
    const pathKey = `${fieldId}-${parentPath.join('-')}-${itemIndex}`
    
    return (
      <div key={itemIndex} className="space-y-2 pl-4 pb-2 border-l-2 border-border-color" style={{ marginLeft: `${depth * 8}px` }}>
        <div className="flex gap-2 items-start">
          <span className="text-xs text-text-secondary min-w-[40px] pt-2">[{itemIndex}]</span>
          <div className="flex-1 space-y-2">
            {/* Type Dropdown */}
            <div className="relative">
              <button
                onClick={() => setOpenFieldTypeDropdowns({...openFieldTypeDropdowns, [pathKey]: !openFieldTypeDropdowns[pathKey]})}
                className="w-full px-3 py-2 dark:bg-surface bg-white border border-border-color rounded flex items-center justify-between hover:border-primary transition-colors dark:text-white text-gray-900"
              >
                <span>{item.itemType || 'string'}</span>
                <ChevronDown className="w-4 h-4" />
              </button>
              {openFieldTypeDropdowns[pathKey] && (
                <div className="absolute z-10 w-full mt-1 dark:bg-surface bg-white border border-border-color rounded-lg shadow-lg">
                  {(['string', 'number', 'boolean', 'array'] as const).map((type) => (
                    <button
                      key={type}
                      onClick={() => {
                        handleUpdateItemType(fieldId, itemIndex, type, parentPath.length > 0 ? parentPath : undefined)
                        setOpenFieldTypeDropdowns({...openFieldTypeDropdowns, [pathKey]: false})
                      }}
                      className="w-full px-3 py-2 text-left hover:bg-hover transition-colors dark:text-white text-gray-900 first:rounded-t-lg last:rounded-b-lg"
                    >
                      {type}
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            {/* Name field */}
            <input
              type="text"
              value={item.key}
              onChange={(e) => handleUpdatePair(fieldId, itemIndex, e.target.value, item.value, parentPath.length > 0 ? parentPath : undefined)}
              placeholder="Item name (optional)"
              className="w-full px-3 py-2 dark:bg-surface bg-white border border-border-color rounded focus:outline-none focus:ring-2 focus:ring-primary dark:text-white text-gray-900"
            />
            
            {/* Value field - different based on type */}
            {item.itemType === 'boolean' ? (
              <select
                value={item.value}
                onChange={(e) => handleUpdatePair(fieldId, itemIndex, item.key, e.target.value, parentPath.length > 0 ? parentPath : undefined)}
                className="w-full px-3 py-2 dark:bg-surface bg-white border border-border-color rounded focus:outline-none focus:ring-2 focus:ring-primary dark:text-white text-gray-900"
              >
                <option value="">Select value</option>
                <option value="true">true</option>
                <option value="false">false</option>
              </select>
            ) : item.itemType === 'array' ? (
              <div className="space-y-2">
                <div className="text-xs text-text-secondary font-semibold">Nested Array Items:</div>
                {(item.nestedItems || []).map((nestedItem, nestedIndex) => 
                  renderArrayItem(nestedItem, nestedIndex, fieldId, [...parentPath, itemIndex], depth + 1)
                )}
                <button
                  onClick={() => handleAddPairToField(fieldId, [...parentPath, itemIndex])}
                  className="flex items-center gap-1 text-sm text-primary hover:text-primary-hover transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Add nested item
                </button>
              </div>
            ) : (
              <input
                type={item.itemType === 'number' ? 'number' : 'text'}
                value={item.value}
                onChange={(e) => handleUpdatePair(fieldId, itemIndex, item.key, e.target.value, parentPath.length > 0 ? parentPath : undefined)}
                placeholder="Item value"
                className="w-full px-3 py-2 dark:bg-surface bg-white border border-border-color rounded focus:outline-none focus:ring-2 focus:ring-primary dark:text-white text-gray-900"
              />
            )}
          </div>
          <button
            onClick={() => handleRemovePairFromField(fieldId, itemIndex, parentPath.length > 0 ? parentPath : undefined)}
            className="p-2 text-red-500 hover:bg-red-500/10 rounded transition-colors"
            title="Remove item"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold">Data Management</h1>
        <p className="text-text-secondary">Upload and manage documents for RAG processing. Category types are loaded from your Data Management Preferences.</p>
      </div>

      {/* Single Item Manager */}
      <Card title="Single Item Manager">
        <div className="space-y-4">
          {/* Category Types Status Bar */}
          {isLoadingPreferences && (
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 flex items-center gap-2">
              <RefreshCw className="w-4 h-4 text-blue-500 animate-spin" />
              <span className="text-sm text-blue-500">Loading category types from preferences...</span>
            </div>
          )}

          {/* Search Section */}
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="text"
              value={searchId}
              onChange={(e) => setSearchId(e.target.value)}
              placeholder="ID"
              className="flex-1 px-4 py-2 dark: dark:bg-background bg-white bg-white border border-border-color rounded-lg focus:outline-none focus:ring-2 focus:ring-primary dark:text-white text-gray-900"
              onKeyDown={(e) => e.key === 'Enter' && handleSearchItem()}
            />
            <div className="relative">
              <select
                value={searchType}
                onChange={(e) => {
                  setSearchType(e.target.value);
                  setSearchTypeDropdownOpen(false);
                }}
                onMouseDown={() => setSearchTypeDropdownOpen(!searchTypeDropdownOpen)}
                onBlur={() => setSearchTypeDropdownOpen(false)}
                className="appearance-none px-4 py-2 pr-10 dark: dark:bg-background bg-white bg-white border border-border-color rounded-lg focus:outline-none focus:ring-2 focus:ring-primary dark:text-white text-gray-900 cursor-pointer"
                disabled={isLoadingPreferences}
              >
                {categoryTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
              <ChevronDown className={`w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none transition-transform ${searchTypeDropdownOpen ? 'rotate-180' : ''}`} />
            </div>
            <Button onClick={handleSearchItem} isLoading={isSearching} disabled={isSearching}>
              <Search className="w-4 h-4 mr-2" />
              Search
            </Button>
          </div>

          {/* Search Result - Item Found */}
          {editedData && !itemNotFound && (
            <div className="border border-border-color rounded-lg p-4 space-y-4">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                <h3 className="text-lg font-semibold dark:text-white text-gray-900 truncate">
                  {searchType}: {editedData.id}
                </h3>
                <div className="flex flex-wrap gap-2">
                  <Button onClick={handleRefreshData} variant="secondary" size="sm">
                    <RefreshCw className="w-4 h-4 sm:mr-1" />
                    <span className="hidden sm:inline">Reset</span>
                  </Button>
                  <Button onClick={handleSaveItem} isLoading={isSaving} disabled={isSaving} size="sm">
                    <Save className="w-4 h-4 sm:mr-1" />
                    <span className="hidden sm:inline">Save</span>
                  </Button>
                  <Button onClick={handleDeleteItem} isLoading={isDeleting} disabled={isDeleting} variant="secondary" size="sm">
                    <Trash2 className="w-4 h-4 sm:mr-1" />
                    <span className="hidden sm:inline">Delete</span>
                  </Button>
                </div>
              </div>

              {/* View Mode Toggle */}
              <div className="flex justify-center">
                <div className="inline-flex rounded-lg border border-border-color bg-surface p-1">
                  <button
                    onClick={() => setViewMode('form')}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      viewMode === 'form'
                        ? 'bg-primary text-white'
                        : 'text-text-secondary hover:text-text-primary'
                    }`}
                  >
                    Form
                  </button>
                  <button
                    onClick={() => setViewMode('json')}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      viewMode === 'json'
                        ? 'bg-primary text-white'
                        : 'text-text-secondary hover:text-text-primary'
                    }`}
                  >
                    JSON
                  </button>
                </div>
              </div>

              {/* Form View */}
              {viewMode === 'form' && (
                <div className="bg-surface border border-border-color rounded-lg p-4">
                  <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
                    {getFlattenedFields(editedData).map((field, index) => (
                      <div key={field.path} className="flex gap-3 items-start">
                        <label className="text-sm font-medium text-text-secondary min-w-[180px] pt-2">
                          {field.label}:
                        </label>
                        <div className="flex-1 flex gap-2 items-center">
                          {field.type === 'primitive' ? (
                            <>
                              {typeof field.value === 'boolean' ? (
                                <select
                                  value={field.value.toString()}
                                  onChange={(e) => handleFormFieldChange(field.path, e.target.value === 'true')}
                                  className="w-full px-3 py-2  dark:bg-background bg-white border border-border-color rounded focus:outline-none focus:ring-2 focus:ring-primary dark:text-white text-gray-900"
                                >
                                  <option value="true">true</option>
                                  <option value="false">false</option>
                                </select>
                              ) : typeof field.value === 'number' ? (
                                <input
                                  type="number"
                                  value={field.value}
                                  onChange={(e) => handleFormFieldChange(field.path, parseFloat(e.target.value) || 0)}
                                  className="w-full px-3 py-2  dark:bg-background bg-white border border-border-color rounded focus:outline-none focus:ring-2 focus:ring-primary dark:text-white text-gray-900"
                                />
                              ) : (
                                <input
                                  type="text"
                                  value={field.value}
                                  onChange={(e) => handleFormFieldChange(field.path, e.target.value)}
                                  className="w-full px-3 py-2  dark:bg-background bg-white border border-border-color rounded focus:outline-none focus:ring-2 focus:ring-primary dark:text-white text-gray-900"
                                />
                              )}
                            </>
                          ) : (
                            <>
                              <div className="flex-1 px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-border-color rounded text-sm text-text-secondary">
                                {field.type === 'array' 
                                  ? `Array [${Array.isArray(field.value) ? field.value.length : 0} items]`
                                  : 'Object {...}'}
                              </div>
                              <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => handleOpenExpandModal(field)}
                                title="Expand to edit"
                              >
                                <ExternalLink className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-text-secondary mt-4">
                    Showing all document fields. Click expand icon to edit complex objects/arrays. Switch to JSON view to edit raw JSON.
                  </p>
                </div>
              )}

              {/* JSON View */}
              {viewMode === 'json' && (
                <div className="bg-surface border border-border-color rounded-lg p-4">
                  <textarea
                    key={'editor_' + resetCounter}
                    value={editedText}
                    onChange={(e) => handleTextChange(e.target.value)}
                    className={`w-full h-96 bg-background border ${jsonError ? 'border-red-500' : 'border-border-color'} rounded p-3 font-mono text-sm dark:text-white text-gray-900 focus:outline-none focus:ring-2 ${jsonError ? 'focus:ring-red-500' : 'focus:ring-primary'} resize-y`}
                    style={{ minHeight: '200px', maxHeight: '600px' }}
                  />
                  {jsonError ? (
                    <p className="text-xs text-red-500 mt-2">
                      ⚠️ JSON Error: {jsonError}
                    </p>
                  ) : (
                    <p className="text-xs text-text-secondary mt-2">
                      Edit the JSON directly. Changes are validated in real-time.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Item Not Found - Add New Item */}
          {itemNotFound && (
            <div className="border border-yellow-500/30 bg-yellow-500/5 rounded-lg p-6 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold dark:text-white text-gray-900">
                    Create New {searchType}: {searchId}
                  </h3>
                  <p className="text-sm text-text-secondary mt-1">
                    Fill in the fields below to create a new document of type: {searchType}
                  </p>
                </div>
              </div>

              {/* Schema Info Banner */}
              {preferencesSchema && newFields.some(f => f.isFromSchema) && (
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                  <p className="text-sm text-blue-400">
                    ✓ Fields loaded from your preferences schema. Schema field names are read-only (marked with "Schema" badge), but you can delete any field or add custom fields.
                  </p>
                </div>
              )}

              {/* Top-level Fields */}
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-text-secondary uppercase tracking-wide">Top-Level Fields</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 sm:items-center">
                    <label className="text-sm font-medium text-text-secondary sm:min-w-[120px]">ID:</label>
                    <input
                      type="text"
                      value={searchId}
                      readOnly
                      className="flex-1 px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-border-color rounded text-sm text-text-secondary"
                    />
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 sm:items-center">
                    <label className="text-sm font-medium text-text-secondary sm:min-w-[120px]">Type:</label>
                    <input
                      type="text"
                      value={searchType.toLowerCase()}
                      readOnly
                      className="flex-1 px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-border-color rounded text-sm text-text-secondary"
                    />
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 sm:items-center">
                    <label className="text-sm font-medium text-text-secondary sm:min-w-[120px]">Name:</label>
                    <input
                      type="text"
                      value={newFields.find(f => f.id === 'top_name')?.value || ''}
                      onChange={(e) => {
                        const existing = newFields.find(f => f.id === 'top_name')
                        if (existing) {
                          handleUpdateNewField('top_name', { value: e.target.value })
                        } else {
                          setNewFields([...newFields, { id: 'top_name', value: e.target.value, dataType: 'string', category: 'Top', pairs: [] }])
                        }
                      }}
                      placeholder={`${searchType} name`}
                      className="flex-1 px-3 py-2 dark: dark:bg-background bg-white bg-white border border-border-color rounded focus:outline-none focus:ring-2 focus:ring-primary dark:text-white text-gray-900"
                    />
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 sm:items-center">
                    <label className="text-sm font-medium text-text-secondary sm:min-w-[120px]">Deleted:</label>
                    <select
                      value={newFields.find(f => f.id === 'top_deleted')?.value || 'false'}
                      onChange={(e) => {
                        const existing = newFields.find(f => f.id === 'top_deleted')
                        if (existing) {
                          handleUpdateNewField('top_deleted', { value: e.target.value })
                        } else {
                          setNewFields([...newFields, { id: 'top_deleted', value: e.target.value, dataType: 'boolean', category: 'Top', pairs: [] }])
                        }
                      }}
                      className="flex-1 px-3 py-2 dark: dark:bg-background bg-white bg-white border border-border-color rounded focus:outline-none focus:ring-2 focus:ring-primary dark:text-white text-gray-900"
                    >
                      <option value="false">false</option>
                      <option value="true">true</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Index Properties (from schema) */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-semibold text-text-secondary uppercase tracking-wide">Index Properties (Searchable)</h4>
                  <Button onClick={() => {
                    const newId = `index_custom_${Date.now()}`;
                    setNewFields([...newFields, { 
                      id: newId, 
                      name: '',
                      value: '', 
                      dataType: 'string', 
                      category: 'Index', 
                      pairs: [],
                      isFromSchema: false,
                      canDelete: true
                    }]);
                  }} variant="secondary" size="sm">
                    <Plus className="w-4 h-4 mr-1" />
                    Add Field
                  </Button>
                </div>
                <div className="space-y-2">
                  {newFields.filter(f => f.category === 'Index').map((field) => (
                    <div key={field.id} className="dark:bg-surface bg-gray-50 p-3 rounded border border-border-color space-y-2">
                      <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                        <div className="flex gap-2 items-center flex-wrap">
                          {field.isFromSchema && (
                            <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-1 rounded whitespace-nowrap" title="From preferences schema">
                              Schema
                            </span>
                          )}
                          <input
                            type="text"
                            value={field.name || ''}
                            onChange={(e) => handleUpdateNewField(field.id, { name: e.target.value })}
                            placeholder="Field name"
                            readOnly={field.isFromSchema}
                            className={`px-3 py-2 ${field.isFromSchema ? 'bg-gray-100 dark:bg-gray-700' : 'dark:bg-background bg-white'} border border-border-color rounded focus:outline-none focus:ring-2 focus:ring-primary dark:text-white text-gray-900 w-full sm:w-auto sm:min-w-[150px]`}
                          />
                          <select
                            value={field.dataType}
                            onChange={(e) => handleUpdateNewField(field.id, { dataType: e.target.value as any })}
                            className="px-3 py-2 dark: dark:bg-background bg-white bg-white border border-border-color rounded focus:outline-none focus:ring-2 focus:ring-primary dark:text-white text-gray-900 w-full sm:w-auto"
                          >
                            <option value="string">String</option>
                            <option value="number">Number</option>
                            <option value="boolean">Boolean</option>
                            <option value="array">Array</option>
                          </select>
                        </div>
                        
                        {field.dataType !== 'array' && (
                          <input
                            type={field.dataType === 'number' ? 'number' : 'text'}
                            value={field.value || ''}
                            onChange={(e) => handleUpdateNewField(field.id, { value: e.target.value })}
                            placeholder={`Enter ${field.name || 'value'}`}
                            className="flex-1 px-3 py-2 dark: dark:bg-background bg-white bg-white border border-border-color rounded focus:outline-none focus:ring-2 focus:ring-primary dark:text-white text-gray-900 w-full"
                          />
                        )}
                        
                        {field.canDelete && (
                          <button
                            onClick={() => handleRemoveNewField(field.id)}
                            className="p-2 text-red-500 hover:bg-red-500/10 rounded transition-colors"
                            title="Remove field"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      
                      {/* Array items */}
                      {field.dataType === 'array' && (
                        <div className="ml-6 space-y-2">
                          {parseArrayValue(field.value || '').map((item, index) => {
                            const path = [index];
                            
                            const renderArrayItem = (value: any, itemPath: number[], depth: number = 0): React.ReactNode => {
                              const isArray = Array.isArray(value);
                              const isObject = typeof value === 'object' && value !== null && !isArray;
                              const pathStr = itemPath.map(p => `[${p}]`).join('');
                              const indentClass = depth > 0 ? 'ml-6' : '';
                              
                              // Extract actual value if it's a named object
                              const actualValue = isObject && 'value' in value ? value.value : value;
                              const itemName = isObject && 'name' in value ? value.name : '';
                              const isActualArray = Array.isArray(actualValue);
                              
                              return (
                                <div key={itemPath.join('-')} className={`space-y-2 ${indentClass}`}>
                                  <div className="flex gap-2 items-start">
                                    <span className="text-xs text-text-secondary min-w-[60px] pt-2 font-mono">{pathStr}</span>
                                    
                                    {/* Name input for arrays and objects */}
                                    {(isActualArray || isObject) && (
                                      <input
                                        type="text"
                                        value={itemName}
                                        onChange={(e) => {
                                          const newObj = { name: e.target.value, value: actualValue };
                                          handleUpdateArrayItem(field.id, itemPath, newObj);
                                        }}
                                        placeholder="Name"
                                        className="w-40 px-3 py-2  dark:bg-background bg-white border border-border-color rounded focus:outline-none focus:ring-2 focus:ring-primary dark:text-white text-gray-900 text-sm"
                                      />
                                    )}
                                    
                                    {/* Type Dropdown */}
                                    <select
                                      value={isActualArray ? 'array' : typeof actualValue}
                                      onChange={(e) => {
                                        const newType = e.target.value as 'string' | 'number' | 'boolean' | 'array';
                                        if (newType !== (isActualArray ? 'array' : typeof actualValue)) {
                                          handleConvertArrayItemType(field.id, itemPath, newType);
                                        }
                                      }}
                                      className="px-3 py-2  dark:bg-background bg-white border border-border-color rounded focus:outline-none focus:ring-2 focus:ring-primary dark:text-white text-gray-900"
                                    >
                                      <option value="string">string</option>
                                      <option value="number">number</option>
                                      <option value="boolean">boolean</option>
                                      <option value="array">array</option>
                                    </select>
                                    
                                    {/* Value Input */}
                                    {!isActualArray && (
                                      <>
                                        {typeof actualValue === 'boolean' ? (
                                          <select
                                            value={String(actualValue)}
                                            onChange={(e) => {
                                              const newVal = e.target.value === 'true';
                                              const toSave = itemName ? { name: itemName, value: newVal } : newVal;
                                              handleUpdateArrayItem(field.id, itemPath, toSave);
                                            }}
                                            className="flex-1 px-3 py-2  dark:bg-background bg-white border border-border-color rounded focus:outline-none focus:ring-2 focus:ring-primary dark:text-white text-gray-900"
                                          >
                                            <option value="true">true</option>
                                            <option value="false">false</option>
                                          </select>
                                        ) : (
                                          <input
                                            type={typeof actualValue === 'number' ? 'number' : 'text'}
                                            value={actualValue ?? ''}
                                            onChange={(e) => {
                                              const newVal = typeof actualValue === 'number' ? Number(e.target.value) : e.target.value;
                                              const toSave = itemName ? { name: itemName, value: newVal } : newVal;
                                              handleUpdateArrayItem(field.id, itemPath, toSave);
                                            }}
                                            placeholder="Item value"
                                            className="flex-1 px-3 py-2  dark:bg-background bg-white border border-border-color rounded focus:outline-none focus:ring-2 focus:ring-primary dark:text-white text-gray-900"
                                          />
                                        )}
                                      </>
                                    )}
                                    
                                    {isActualArray && (
                                      <div className="flex-1 px-3 py-2 bg-primary/5 border border-primary/20 rounded text-sm text-primary font-medium">
                                        Array ({actualValue.length} items)
                                      </div>
                                    )}
                                    
                                    <button
                                      onClick={() => handleRemoveArrayItem(field.id, itemPath)}
                                      className="p-2 text-red-500 hover:bg-red-500/10 rounded transition-colors"
                                      title="Remove item"
                                    >
                                      <X className="w-4 h-4" />
                                    </button>
                                  </div>
                                  
                                  {/* Nested array items */}
                                  {isActualArray && (
                                    <div className="ml-6 space-y-2 border-l-2 border-primary/20 pl-4">
                                      {actualValue.map((nestedItem: any, nestedIndex: number) => 
                                        renderArrayItem(nestedItem, [...itemPath, nestedIndex], depth + 1)
                                      )}
                                      <button
                                        onClick={() => handleAddArrayItem(field.id, itemPath)}
                                        className="flex items-center gap-1 text-sm text-primary hover:text-primary-hover transition-colors"
                                      >
                                        <Plus className="w-4 h-4" />
                                        Add nested item
                                      </button>
                                    </div>
                                  )}
                                </div>
                              );
                            };
                            
                            return renderArrayItem(item, path);
                          })}
                          <button
                            onClick={() => handleAddArrayItem(field.id, [])}
                            className="flex items-center gap-1 text-sm text-primary hover:text-primary-hover transition-colors"
                          >
                            <Plus className="w-4 h-4" />
                            Add array item
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                  {newFields.filter(f => f.category === 'Index').length === 0 && (
                    <p className="text-sm text-text-secondary italic">No index properties defined. Click "Add Field" to add one.</p>
                  )}
                </div>
              </div>

              {/* Properties (from schema) */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-semibold text-text-secondary uppercase tracking-wide">Properties (Metadata)</h4>
                  <Button onClick={() => {
                    const newId = `prop_custom_${Date.now()}`;
                    setNewFields([...newFields, { 
                      id: newId, 
                      name: '',
                      value: '', 
                      dataType: 'string', 
                      category: 'Properties', 
                      pairs: [],
                      isFromSchema: false,
                      canDelete: true
                    }]);
                  }} variant="secondary" size="sm">
                    <Plus className="w-4 h-4 mr-1" />
                    Add Field
                  </Button>
                </div>
                <div className="space-y-2">
                  {newFields.filter(f => f.category === 'Properties').map((field) => (
                    <div key={field.id} className="dark:bg-surface bg-gray-50 p-3 rounded border border-border-color space-y-2">
                      <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                        <div className="flex gap-2 items-center flex-wrap">
                          {field.isFromSchema && (
                            <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-1 rounded whitespace-nowrap" title="From preferences schema">
                              Schema
                            </span>
                          )}
                          <input
                            type="text"
                            value={field.name || ''}
                            onChange={(e) => handleUpdateNewField(field.id, { name: e.target.value })}
                            placeholder="Field name"
                            readOnly={field.isFromSchema}
                            className={`px-3 py-2 ${field.isFromSchema ? 'bg-gray-100 dark:bg-gray-700' : 'dark:bg-background bg-white'} border border-border-color rounded focus:outline-none focus:ring-2 focus:ring-primary dark:text-white text-gray-900 w-full sm:w-auto sm:min-w-[150px]`}
                          />
                          <select
                            value={field.dataType}
                            onChange={(e) => handleUpdateNewField(field.id, { dataType: e.target.value as any })}
                            className="px-3 py-2 dark:bg-background bg-white border border-border-color rounded focus:outline-none focus:ring-2 focus:ring-primary dark:text-white text-gray-900 w-full sm:w-auto"
                          >
                            <option value="string">String</option>
                            <option value="number">Number</option>
                            <option value="boolean">Boolean</option>
                            <option value="array">Array</option>
                          </select>
                        </div>
                        
                        {field.dataType !== 'array' && (
                          <input
                            type={field.dataType === 'number' ? 'number' : field.dataType === 'boolean' ? 'checkbox' : 'text'}
                            checked={field.dataType === 'boolean' ? field.value === 'true' : undefined}
                            value={field.dataType === 'boolean' ? undefined : (field.value || '')}
                            onChange={(e) => {
                              const newValue = field.dataType === 'boolean' ? String(e.target.checked) : e.target.value;
                              handleUpdateNewField(field.id, { value: newValue });
                            }}
                            placeholder={`Enter ${field.name || 'value'}`}
                            className="flex-1 px-3 py-2 dark:bg-background bg-white border border-border-color rounded focus:outline-none focus:ring-2 focus:ring-primary dark:text-white text-gray-900"
                          />
                        )}
                        
                        {field.canDelete && (
                          <button
                            onClick={() => handleRemoveNewField(field.id)}
                            className="p-2 text-red-500 hover:bg-red-500/10 rounded transition-colors"
                            title="Remove field"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      
                      {/* Array items */}
                      {field.dataType === 'array' && (
                        <div className="ml-6 space-y-2">
                          {parseArrayValue(field.value || '').map((item, index) => {
                            const path = [index];
                            
                            const renderArrayItem = (value: any, itemPath: number[], depth: number = 0): React.ReactNode => {
                              const isArray = Array.isArray(value);
                              const isObject = typeof value === 'object' && value !== null && !isArray;
                              const pathStr = itemPath.map(p => `[${p}]`).join('');
                              const indentClass = depth > 0 ? 'ml-6' : '';
                              
                              // Extract actual value if it's a named object
                              const actualValue = isObject && 'value' in value ? value.value : value;
                              const itemName = isObject && 'name' in value ? value.name : '';
                              const isActualArray = Array.isArray(actualValue);
                              
                              return (
                                <div key={itemPath.join('-')} className={`space-y-2 ${indentClass}`}>
                                  <div className="flex gap-2 items-start">
                                    <span className="text-xs text-text-secondary min-w-[60px] pt-2 font-mono">{pathStr}</span>
                                    
                                    {/* Name input for arrays and objects */}
                                    {(isActualArray || isObject) && (
                                      <input
                                        type="text"
                                        value={itemName}
                                        onChange={(e) => {
                                          const newObj = { name: e.target.value, value: actualValue };
                                          handleUpdateArrayItem(field.id, itemPath, newObj);
                                        }}
                                        placeholder="Name"
                                        className="w-32 px-3 py-2  dark:bg-background bg-white border border-border-color rounded focus:outline-none focus:ring-2 focus:ring-primary dark:text-white text-gray-900 text-sm"
                                      />
                                    )}
                                    
                                    {/* Type Dropdown */}
                                    <select
                                      value={isActualArray ? 'array' : typeof actualValue}
                                      onChange={(e) => {
                                        const newType = e.target.value as 'string' | 'number' | 'boolean' | 'array';
                                        if (newType !== (isActualArray ? 'array' : typeof actualValue)) {
                                          handleConvertArrayItemType(field.id, itemPath, newType);
                                        }
                                      }}
                                      className="px-3 py-2  dark:bg-background bg-white border border-border-color rounded focus:outline-none focus:ring-2 focus:ring-primary dark:text-white text-gray-900"
                                    >
                                      <option value="string">string</option>
                                      <option value="number">number</option>
                                      <option value="boolean">boolean</option>
                                      <option value="array">array</option>
                                    </select>
                                    
                                    {/* Value Input */}
                                    {!isActualArray && (
                                      <>
                                        {typeof actualValue === 'boolean' ? (
                                          <select
                                            value={String(actualValue)}
                                            onChange={(e) => {
                                              const newVal = e.target.value === 'true';
                                              const toSave = itemName ? { name: itemName, value: newVal } : newVal;
                                              handleUpdateArrayItem(field.id, itemPath, toSave);
                                            }}
                                            className="flex-1 px-3 py-2  dark:bg-background bg-white border border-border-color rounded focus:outline-none focus:ring-2 focus:ring-primary dark:text-white text-gray-900"
                                          >
                                            <option value="true">true</option>
                                            <option value="false">false</option>
                                          </select>
                                        ) : (
                                          <input
                                            type={typeof actualValue === 'number' ? 'number' : 'text'}
                                            value={actualValue ?? ''}
                                            onChange={(e) => {
                                              const newVal = typeof actualValue === 'number' ? Number(e.target.value) : e.target.value;
                                              const toSave = itemName ? { name: itemName, value: newVal } : newVal;
                                              handleUpdateArrayItem(field.id, itemPath, toSave);
                                            }}
                                            placeholder="Item value"
                                            className="flex-1 px-3 py-2  dark:bg-background bg-white border border-border-color rounded focus:outline-none focus:ring-2 focus:ring-primary dark:text-white text-gray-900"
                                          />
                                        )}
                                      </>
                                    )}
                                    
                                    {isActualArray && (
                                      <div className="flex-1 px-3 py-2 bg-primary/5 border border-primary/20 rounded text-sm text-primary font-medium">
                                        Array ({actualValue.length} items)
                                      </div>
                                    )}
                                    
                                    <button
                                      onClick={() => handleRemoveArrayItem(field.id, itemPath)}
                                      className="p-2 text-red-500 hover:bg-red-500/10 rounded transition-colors"
                                      title="Remove item"
                                    >
                                      <X className="w-4 h-4" />
                                    </button>
                                  </div>
                                  
                                  {/* Nested array items */}
                                  {isActualArray && (
                                    <div className="ml-6 space-y-2 border-l-2 border-primary/20 pl-4">
                                      {actualValue.map((nestedItem: any, nestedIndex: number) => 
                                        renderArrayItem(nestedItem, [...itemPath, nestedIndex], depth + 1)
                                      )}
                                      <button
                                        onClick={() => handleAddArrayItem(field.id, itemPath)}
                                        className="flex items-center gap-1 text-sm text-primary hover:text-primary-hover transition-colors"
                                      >
                                        <Plus className="w-4 h-4" />
                                        Add nested item
                                      </button>
                                    </div>
                                  )}
                                </div>
                              );
                            };
                            
                            return renderArrayItem(item, path);
                          })}
                          <button
                            onClick={() => handleAddArrayItem(field.id, [])}
                            className="flex items-center gap-1 text-sm text-primary hover:text-primary-hover transition-colors"
                          >
                            <Plus className="w-4 h-4" />
                            Add array item
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                  {newFields.filter(f => f.category === 'Properties').length === 0 && (
                    <p className="text-sm text-text-secondary italic">No metadata properties defined. Click "Add Field" to add one.</p>
                  )}
                </div>
              </div>
              
              <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 pt-2">
                <Button onClick={() => { setItemNotFound(false); setNewFields([]) }} variant="secondary">
                  Cancel
                </Button>
                <Button 
                  onClick={handleCreateNewItem} 
                  isLoading={isSaving}
                  disabled={isSaving}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Item
                </Button>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Expand Modal for Complex Objects/Arrays */}
      {expandModalData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="dark:bg-background bg-white border border-border-color rounded-lg shadow-xl w-full max-w-sm sm:max-w-2xl lg:max-w-4xl max-h-[80vh] flex flex-col">
            <div className="p-4 border-b border-border-color flex justify-between items-center">
              <h3 className="text-lg font-semibold dark:text-white text-gray-900 truncate pr-4">
                Edit: {expandModalData.label}
              </h3>
              <button
                onClick={handleCloseExpandModal}
                className="p-2 hover:bg-surface rounded transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 p-4 overflow-y-auto">
              {/* Array Display */}
              {Array.isArray(expandedValue) && (
                <div className="space-y-4">
                  {expandedValue.map((item, index) => (
                    <div key={index} className="border border-border-color rounded-lg p-4 bg-surface">
                      <div className="flex justify-between items-center mb-3">
                        <h4 className="text-sm font-semibold dark:text-white text-gray-900">
                          Item {index + 1}
                        </h4>
                        <button
                          onClick={() => handleRemoveExpandedArrayItem(index)}
                          className="p-1 text-red-500 hover:bg-red-500/10 rounded transition-colors"
                          title="Remove item"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="space-y-3">
                        {typeof item === 'object' && item !== null ? (
                          Object.entries(item).map(([key, value]) => (
                            <div key={key} className="flex gap-3 items-center">
                              <label className="text-sm font-medium text-text-secondary min-w-[120px]">
                                {key}:
                              </label>
                              <div className="flex-1">
                                {renderExpandedFieldValue(value, index, key)}
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="flex gap-3 items-center">
                            <label className="text-sm font-medium text-text-secondary min-w-[120px]">
                              Value:
                            </label>
                            <div className="flex-1">
                              {renderExpandedFieldValue(item, index, 'value')}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  <Button onClick={handleAddExpandedArrayItem} variant="secondary" size="sm">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Item
                  </Button>
                </div>
              )}

              {/* Object Display */}
              {!Array.isArray(expandedValue) && typeof expandedValue === 'object' && expandedValue !== null && (
                <div className="space-y-3">
                  {Object.entries(expandedValue).map(([key, value]) => {
                    const isDisabled = isFieldDisabledInRaw(key)
                    const isComplex = typeof value === 'object' && value !== null
                    
                    return (
                      <div key={key} className="flex gap-3 items-center">
                        <label className="text-sm font-medium text-text-secondary min-w-[180px]">
                          {key}:
                          {isDisabled && <span className="ml-2 text-xs text-yellow-500">(managed outside)</span>}
                        </label>
                        <div className="flex-1 flex gap-2">
                          {typeof value === 'boolean' ? (
                            <select
                              value={value.toString()}
                              onChange={(e) => handleUpdateExpandedField(key, key, e.target.value === 'true')}
                              disabled={isDisabled}
                              className="w-full px-3 py-2  dark:bg-background bg-white border border-border-color rounded focus:outline-none focus:ring-2 focus:ring-primary dark:text-white text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <option value="true">true</option>
                              <option value="false">false</option>
                            </select>
                          ) : typeof value === 'number' ? (
                            <input
                              type="number"
                              value={value}
                              onChange={(e) => handleUpdateExpandedField(key, key, parseFloat(e.target.value) || 0)}
                              disabled={isDisabled}
                              className="w-full px-3 py-2  dark:bg-background bg-white border border-border-color rounded focus:outline-none focus:ring-2 focus:ring-primary dark:text-white text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                            />
                          ) : typeof value === 'string' ? (
                            <input
                              type="text"
                              value={value}
                              onChange={(e) => handleUpdateExpandedField(key, key, e.target.value)}
                              disabled={isDisabled}
                              className="w-full px-3 py-2  dark:bg-background bg-white border border-border-color rounded focus:outline-none focus:ring-2 focus:ring-primary dark:text-white text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                            />
                          ) : value === null || value === undefined ? (
                            <input
                              type="text"
                              value=""
                              readOnly
                              placeholder="null"
                              className="w-full px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-border-color rounded text-sm text-text-secondary"
                            />
                          ) : (
                            <>
                              <div className="flex-1 px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-border-color rounded text-sm text-text-secondary">
                                {Array.isArray(value) ? `Array [${value.length} items]` : 'Object'}
                              </div>
                              {!isDisabled && (
                                <button
                                  onClick={() => handleOpenNestedExpandModal(key, value)}
                                  className="px-3 py-2 bg-primary text-white rounded hover:bg-primary/90 transition-colors flex items-center gap-2"
                                  title="Expand and edit"
                                >
                                  <ExternalLink className="w-4 h-4" />
                                  Expand
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}

              {/* Primitive Value Display */}
              {!Array.isArray(expandedValue) && typeof expandedValue !== 'object' && (
                <div className="flex gap-3 items-center">
                  <label className="text-sm font-medium text-text-secondary min-w-[120px]">
                    Value:
                  </label>
                  <div className="flex-1">
                    <input
                      type="text"
                      value={String(expandedValue)}
                      readOnly
                      className="w-full px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-border-color rounded text-sm text-text-secondary"
                    />
                  </div>
                </div>
              )}
            </div>
            <div className="p-4 border-t border-border-color flex justify-end gap-2">
              <Button variant="secondary" onClick={handleCloseExpandModal}>
                Cancel
              </Button>
              <Button onClick={handleSaveExpandedModal}>
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Nested Expand Modal (stacked on top) */}
      {nestedModalData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="dark:bg-background bg-white border border-border-color rounded-lg shadow-xl w-full max-w-sm sm:max-w-2xl lg:max-w-4xl max-h-[80vh] flex flex-col">
            <div className="p-4 border-b border-border-color flex justify-between items-center">
              <h3 className="text-lg font-semibold dark:text-white text-gray-900 truncate pr-4">
                Edit: {nestedModalData.label}
              </h3>
              <button
                onClick={handleCloseNestedExpandModal}
                className="p-2 hover:bg-surface rounded transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 p-4 overflow-y-auto">
              {/* Array Display */}
              {Array.isArray(nestedExpandedValue) && (
                <div className="space-y-4">
                  {nestedExpandedValue.map((item, index) => (
                    <div key={index} className="border border-border-color rounded-lg p-4 bg-surface">
                      <div className="flex justify-between items-center mb-3">
                        <h4 className="text-sm font-semibold dark:text-white text-gray-900">
                          Item {index + 1}
                        </h4>
                        <button
                          onClick={() => handleRemoveNestedExpandedArrayItem(index)}
                          className="p-1 text-red-500 hover:bg-red-500/10 rounded transition-colors"
                          title="Remove item"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="space-y-3">
                        {typeof item === 'object' && item !== null ? (
                          Object.entries(item).map(([key, value]) => (
                            <div key={key} className="flex gap-3 items-center">
                              <label className="text-sm font-medium text-text-secondary min-w-[120px]">
                                {key}:
                              </label>
                              <div className="flex-1">
                                {typeof value === 'boolean' ? (
                                  <select
                                    value={value.toString()}
                                    onChange={(e) => handleUpdateNestedExpandedField(index, key, e.target.value === 'true')}
                                    className="w-full px-3 py-2  dark:bg-background bg-white border border-border-color rounded focus:outline-none focus:ring-2 focus:ring-primary dark:text-white text-gray-900"
                                  >
                                    <option value="true">true</option>
                                    <option value="false">false</option>
                                  </select>
                                ) : typeof value === 'number' ? (
                                  <input
                                    type="number"
                                    value={value}
                                    onChange={(e) => handleUpdateNestedExpandedField(index, key, parseFloat(e.target.value) || 0)}
                                    className="w-full px-3 py-2  dark:bg-background bg-white border border-border-color rounded focus:outline-none focus:ring-2 focus:ring-primary dark:text-white text-gray-900"
                                  />
                                ) : typeof value === 'string' ? (
                                  <input
                                    type="text"
                                    value={value}
                                    onChange={(e) => handleUpdateNestedExpandedField(index, key, e.target.value)}
                                    className="w-full px-3 py-2  dark:bg-background bg-white border border-border-color rounded focus:outline-none focus:ring-2 focus:ring-primary dark:text-white text-gray-900"
                                  />
                                ) : (
                                  <div className="px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-border-color rounded text-sm text-text-secondary">
                                    {typeof value === 'object' && value !== null
                                      ? Array.isArray(value) ? `Nested Array [${value.length} items]` : 'Nested Object'
                                      : String(value)}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="flex gap-3 items-center">
                            <label className="text-sm font-medium text-text-secondary min-w-[120px]">
                              Value:
                            </label>
                            <div className="flex-1">
                              <input
                                type="text"
                                value={String(item)}
                                onChange={(e) => handleUpdateNestedExpandedField(index, 'value', e.target.value)}
                                className="w-full px-3 py-2  dark:bg-background bg-white border border-border-color rounded focus:outline-none focus:ring-2 focus:ring-primary dark:text-white text-gray-900"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  <Button onClick={handleAddNestedExpandedArrayItem} variant="secondary" size="sm">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Item
                  </Button>
                </div>
              )}

              {/* Object Display */}
              {!Array.isArray(nestedExpandedValue) && typeof nestedExpandedValue === 'object' && nestedExpandedValue !== null && (
                <div className="space-y-3">
                  {Object.entries(nestedExpandedValue).map(([key, value]) => (
                    <div key={key} className="flex gap-3 items-center">
                      <label className="text-sm font-medium text-text-secondary min-w-[180px]">
                        {key}:
                      </label>
                      <div className="flex-1">
                        {typeof value === 'boolean' ? (
                          <select
                            value={value.toString()}
                            onChange={(e) => handleUpdateNestedExpandedField(key, key, e.target.value === 'true')}
                            className="w-full px-3 py-2  dark:bg-background bg-white border border-border-color rounded focus:outline-none focus:ring-2 focus:ring-primary dark:text-white text-gray-900"
                          >
                            <option value="true">true</option>
                            <option value="false">false</option>
                          </select>
                        ) : typeof value === 'number' ? (
                          <input
                            type="number"
                            value={value}
                            onChange={(e) => handleUpdateNestedExpandedField(key, key, parseFloat(e.target.value) || 0)}
                            className="w-full px-3 py-2  dark:bg-background bg-white border border-border-color rounded focus:outline-none focus:ring-2 focus:ring-primary dark:text-white text-gray-900"
                          />
                        ) : typeof value === 'string' ? (
                          <input
                            type="text"
                            value={value}
                            onChange={(e) => handleUpdateNestedExpandedField(key, key, e.target.value)}
                            className="w-full px-3 py-2  dark:bg-background bg-white border border-border-color rounded focus:outline-none focus:ring-2 focus:ring-primary dark:text-white text-gray-900"
                          />
                        ) : value === null || value === undefined ? (
                          <input
                            type="text"
                            value=""
                            readOnly
                            placeholder="null"
                            className="w-full px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-border-color rounded text-sm text-text-secondary"
                          />
                        ) : (
                          <div className="w-full px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-border-color rounded text-sm text-text-secondary">
                            {Array.isArray(value) ? `Nested Array [${value.length} items]` : 'Nested Object'}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Primitive Value Display */}
              {!Array.isArray(nestedExpandedValue) && typeof nestedExpandedValue !== 'object' && (
                <div className="flex gap-3 items-center">
                  <label className="text-sm font-medium text-text-secondary min-w-[120px]">
                    Value:
                  </label>
                  <div className="flex-1">
                    <input
                      type="text"
                      value={String(nestedExpandedValue)}
                      readOnly
                      className="w-full px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-border-color rounded text-sm text-text-secondary"
                    />
                  </div>
                </div>
              )}
            </div>
            <div className="p-4 border-t border-border-color flex justify-end gap-2">
              <Button variant="secondary" onClick={handleCloseNestedExpandModal}>
                Cancel
              </Button>
              <Button onClick={handleSaveNestedExpandedModal}>
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </Button>
            </div>
          </div>
        </div>
      )}

      <Card title="Batch Document Import (CSV/JSON/XML)">
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-2">
            <Button onClick={handleImportStartBatch} isLoading={importIsReserving} disabled={importIsReserving || !!importBatchId}>
              {importBatchId ? 'Import Batch Reserved' : 'Start Import Batch'}
            </Button>
            {importBatchId && (
              <span className="text-sm text-text-secondary">Batch: {importBatchId}</span>
            )}
          </div>

          <div className="flex flex-col lg:flex-row items-stretch lg:items-center gap-4">
            <div className="flex-1">
              <div
                className="flex justify-center w-full h-32 px-6 transition  dark:bg-background bg-white border-2 border-border-color border-dashed rounded-md appearance-none cursor-pointer hover:border-primary focus:outline-none"
                onClick={() => fileInputRef.current?.click()}
                onDragOver={handleDragOver}
                onDrop={(e) => {
                  e.preventDefault(); e.stopPropagation();
                  const dropped = Array.from(e.dataTransfer.files || [])
                  const allowed = dropped.filter(f => /\.(csv|json|xml)$/i.test(f.name))
                  if (allowed.length === 0) { showToast('Please drop CSV/JSON/XML files', 'error'); return }
                  setImportFiles(allowed)
                  const initProg: Record<string, number> = {}
                  allowed.forEach(f => (initProg[f.name] = 0))
                  setImportProgress(initProg)
                }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click() }}
              >
                <span className="flex items-center space-x-2">
                  <Upload className="w-6 h-6 text-text-secondary"/>
                  <span className="font-medium text-text-secondary text-center sm:text-left">
                    {importFiles.length > 0 ? `${importFiles.length} file(s) selected` : 'Drop CSV/JSON/XML files here, or click to select'}
                    <span className="text-xs block">CSV, JSON, XML files</span>
                    {!importBatchId && (
                      <span className="text-xs text-yellow-400 block mt-1">Start Import Batch before uploading.</span>
                    )}
                  </span>
                </span>
                <input
                  ref={fileInputRef}
                  id="import-file-upload"
                  type="file"
                  multiple
                  onChange={handleImportFilesChange}
                  accept=".csv,application/json,.json,.xml,application/xml,text/xml,text/csv"
                  aria-label="Choose CSV/JSON/XML files"
                  style={{ position: 'absolute', opacity: 0, width: 1, height: 1, left: -9999 }}
                />
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 lg:gap-2 items-stretch sm:items-center">
              <div className="flex items-center gap-3">
                <label className="text-sm text-text-secondary whitespace-nowrap">Format hint:</label>
                <div className="relative flex-1 sm:flex-initial">
                  <select 
                    className="appearance-none w-full sm:w-auto dark:bg-background bg-white border border-border-color rounded px-2 py-1 pr-8 text-sm cursor-pointer"
                    value={importFormat} 
                    onChange={(e) => {
                      setImportFormat(e.target.value as any);
                      setImportTypeDropdownOpen(false);
                    }}
                    onMouseDown={() => setImportTypeDropdownOpen(!importTypeDropdownOpen)}
                    onBlur={() => setImportTypeDropdownOpen(false)}
                  >
                    <option value="auto">auto</option>
                    <option value="csv">csv</option>
                    <option value="json">json</option>
                    <option value="xml">xml</option>
                  </select>
                  <ChevronDown className={`w-3 h-3 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none transition-transform ${importTypeDropdownOpen ? 'rotate-180' : ''}`} />
                </div>
              </div>

              <Button onClick={() => handleImportUploadAll()} disabled={!importBatchId || importFiles.length === 0 || importIsUploading} isLoading={importIsUploading}>
                Upload Selected Files
              </Button>
              <Button onClick={handleTriggerImport} disabled={importUploadedKeys.length === 0 || importIsTriggering} isLoading={importIsTriggering}>
                Trigger Import
              </Button>
            </div>
          </div>

          {importFiles.length > 0 && (
            <div className="space-y-2">
              {importFiles.map(f => (
                <div key={f.name}>
                  <div className="flex justify-between text-sm">
                    <span className="text-text-primary">{f.name}</span>
                    <span className="text-text-secondary">{importProgress[f.name] ?? 0}%</span>
                  </div>
                  <ProgressBar value={importProgress[f.name] ?? 0} />
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>

      {/* Toast Notification */}
      {toast && (
        <Toast 
          message={toast.message} 
          type={toast.type} 
          onClose={() => setToast(null)} 
        />
      )}
    </div>
  );
};

export default DataManagementPage;
